// History/context of this util https://github.com/lessonnine/design-system.lib/pull/548
import { useCallback, useEffect, useMemo, useState } from 'react';

import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';
import { ResponsiveProp } from './responsiveProp';

const breakpoints = tokens.viewports.breakpoints;

type Breakpoint = keyof typeof breakpoints;

const getScreenSize = (width: number): Breakpoint => {
  if (width >= parseFloat(breakpoints.large)) return 'large';
  if (width >= parseFloat(breakpoints.medium)) return 'medium';
  if (width >= parseFloat(breakpoints.small)) return 'small';
  if (width >= parseFloat(breakpoints.xsmall)) return 'xsmall';
  if (width >= parseFloat(breakpoints.xxsmall)) return 'xxsmall';
  return 'xxxsmall';
};

export const useMedia = () => {
  // `window` is undefined on SSR, so it must be checked if it exists before calling it or the SSR build will fail!
  const isBrowser = typeof window !== 'undefined';
  const [width, setWidth] = useState<number>(
    isBrowser ? window.innerWidth : parseFloat(breakpoints.small)
  );

  useEffect(() => {
    // eslint-disable-next-line compat/compat
    const observer = new ResizeObserver(() => {
      setWidth(window.innerWidth);
    });

    observer.observe(document.body);

    return () => observer.disconnect();
  }, []);

  // Returns a breakpoint key
  const screenSize = useMemo(() => getScreenSize(width), [width]);

  // Based on the list of values that a parameter can have this function
  // will return the matching value based on the breakpoint
  const getCurrentValue = useCallback(
    <T>(values: ResponsiveProp<any>, base: T): T => {
      let value: T = base;
      const breakpoints = (Object.keys(tokens.viewports.breakpoints) as Breakpoint[]).reduce(
        (result, current) => {
          typeof values === 'object'
            ? values[current]
              ? (value = values[current])
              : undefined
            : (value = values);
          return { ...result, [current]: value };
        },
        {} as Record<Breakpoint, T>
      );
      return breakpoints[screenSize];
    },
    [screenSize]
  );

  return { screenSize, getCurrentValue };
};
